import { Spinner } from "@/components/Spinner";
import { BaseLivlyApiResponse } from "@/types/Base";
import { BASE_API_URL } from "@/utils/constants";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Outlet } from "react-router";
import { useRouteParams } from "@/hooks/useEncodedIds";

export type Vendor = {
  _createdAt: Date;
  _id: string;
  _rev: string;
  _type: string;
  _updatedAt: Date;
  vendorBadge: string;
  vendorCtaText: string;
  vendorLogo: string;
  vendorName: string;
  vendorSubtitle: string;
  vendorOffers: VendorOffer[];
};

export type VendorOffer = {
  _createdAt: Date;
  _id: string;
  _rev: string;
  _type: string;
  _updatedAt: Date;
  exludedClients: number[];
  offerBannerImage: string | null;
  offerCtaText: string;
  offerDescription: OfferDescription[];
  offerEmailLivly: string;
  offerExternalLink: string;
  offerSquareImage: string;
  offerSubTitle: string;
  offerTitle: string;
  priority: number;
  vendor: {
    _ref: string;
    _type: string;
  };
};

export type OfferDescription = {
  _key: string;
  _type: string;
  children: any;
};

export type MarketplaceCategory = {
  externalId: number;
  imageUrl: string;
  title: string;
};

async function getVendors(leaseId: string) {
  const result = await axios.get<BaseLivlyApiResponse<Vendor[]>>(
    `${BASE_API_URL}/livly/common/content?leaseId=${leaseId}`
  );

  return result.data.Data;
}

async function getCategories(leaseId: string) {
  const result = await axios.get<BaseLivlyApiResponse<MarketplaceCategory[]>>(
    `${BASE_API_URL}/livly/buildingOffer/lease/${leaseId}/category`
  );

  return result.data.Data;
}

async function getCategoryOffers(leaseId: string, categoryId: string) {
  const result = await axios.get(
    `${BASE_API_URL}/livly/buildingOffer/lease/${leaseId}/category/${categoryId}/offers`
  );

  return result.data.Data;
}

async function getOffersWithoutCategory(leaseId: string) {
  const result = await axios.get(
    `${BASE_API_URL}/livly/buildingOffer/lease/${leaseId}/offersWithoutCategory`
  );

  return result.data.Data;
}

const marketplaceVendorsQuery = (leaseId: string) => ({
  queryKey: ["building-offers", leaseId],
  queryFn: async () => getVendors(leaseId)
});

const marketplaceCategoriesQuery = (leaseId: string) => ({
  queryKey: ["building-offer-categories", leaseId],
  queryFn: async () => getCategories(leaseId)
});

const marketplaceCategoryOffersQuery = (
  leaseId: string,
  categoryId: string
) => ({
  queryKey: ["building-offer-category-offers", leaseId, categoryId],
  queryFn: async () => getCategoryOffers(leaseId, categoryId)
});

const marketplaceOffersWithoutCategoryQuery = (leaseId: string) => ({
  queryKey: ["building-offers-without-category", leaseId],
  queryFn: async () => getOffersWithoutCategory(leaseId)
});

export type MarketplaceContext = {
  data: Vendor[];
  isLoading: boolean;
  categories: MarketplaceCategory[];
};

export function useGetMarketplaceOffers(leaseId: string | number) {
  return useQuery({
    ...marketplaceVendorsQuery(leaseId.toString())
  });
}

export function useGetMarketplaceCategories(leaseId: string | number) {
  return useQuery({
    ...marketplaceCategoriesQuery(leaseId.toString())
  });
}

export function useGetMarketplaceCategoryOffers(
  leaseId: string | number,
  categoryId: string | number | null
) {
  const isEnabled = categoryId !== "null" && categoryId !== null;
  return useQuery({
    ...marketplaceCategoryOffersQuery(
      leaseId.toString(),
      (categoryId || "0")?.toString()
    ),
    enabled: isEnabled
  });
}

export function useGetOffersWithoutCategory(leaseId: string | number) {
  return useQuery({
    ...marketplaceOffersWithoutCategoryQuery(leaseId.toString())
  });
}

export default function MarketplaceLayout() {
  const params = useRouteParams<{ leaseId: string }>();

  const { data = [], isLoading } = useGetMarketplaceOffers(params.leaseId!);
  const { data: categories = [], isLoading: isLoadingCategories } =
    useGetMarketplaceCategories(params.leaseId!);

  const context: MarketplaceContext = {
    data,
    isLoading: isLoading || isLoadingCategories,
    categories
  };

  return <Outlet context={context} />;
}
